import type { FlexibleAuditSession } from '@internal-audit/domain/graphql-types';
import { Heading, Text } from '@soteria/design-system-ve';

import { ProgressBar } from '../../../core/components';

import * as styles from './AuditItemSection.css';

interface ProgressStatsProps {
  stats?: { totalCount: number; completeCount: number; skippedCount: number; incompleteCount: number } | null;
}

interface AuditItemSectionHeaderProps {
  stats?: { totalCount: number; completeCount: number; skippedCount: number; incompleteCount: number } | null;
  sessionProgressPercentage: number;
  session?: FlexibleAuditSession | null;
}

/**
 * Progress statistics display component
 * Shows total, completed, skipped, and remaining item counts
 */
function ProgressStats({ stats }: ProgressStatsProps) {
  return (
    <div className={styles.progressStats}>
      <div className={styles.progressStat}>
        <Text className={styles.statLabel}>Total Items</Text>
        <Text className={styles.statValue}>{stats?.totalCount || 0}</Text>
      </div>
      <div className={styles.progressStat}>
        <Text className={styles.statLabel}>Completed</Text>
        <Text className={styles.statValue}>{stats?.completeCount || 0}</Text>
      </div>
      <div className={styles.progressStat}>
        <Text className={styles.statLabel}>Skipped</Text>
        <Text className={styles.statValue}>{stats?.skippedCount || 0}</Text>
      </div>
      <div className={styles.progressStat}>
        <Text className={styles.statLabel}>Remaining</Text>
        <Text className={styles.statValue}>{stats?.incompleteCount || 0}</Text>
      </div>
    </div>
  );
}

/**
 * Section header component with title, progress stats, and progress bar
 */
export function AuditItemSectionHeader({ stats, sessionProgressPercentage, session }: AuditItemSectionHeaderProps) {
  return (
    <div data-audit-items-header className={styles.sectionHeader}>
      <div className={styles.sectionHeaderLeft}>
        <Heading level="h3">Audit Items</Heading>
      </div>
      <ProgressStats stats={stats} />
      <div className={styles.progressInfo}>
        <ProgressBar
          animated
          aria-label={`Audit progress: ${sessionProgressPercentage}% complete`}
          color="primary"
          size="medium"
          text={stats || session ? `${sessionProgressPercentage}% Complete` : 'No session data'}
          textAlign="right"
          textPosition="before"
          value={sessionProgressPercentage}
        />
      </div>
    </div>
  );
}
